import React, {useEffect, useRef, useState} from 'react';
import {FaChevronDown, FaLocationArrow, FaMapMarkerAlt, FaTimes} from 'react-icons/fa';
import {GeocodeSuggestion, geocodeQuery} from '../../api/geocoding';
import {useLocationContext} from '../../providers/LocationProvider';
import {useScreenSizeContext} from '../../providers/ScreenSizeProvider';
import './LocationPicker.css';

interface LocationPickerProps {
    variant?: 'default' | 'inline';
    placeholderLabel?: string;
}

const LocationPicker: React.FC<LocationPickerProps> = ({variant = 'default', placeholderLabel = 'Choose Location'}) => {
    const {location, setLocation, isLocating, requestCurrentLocation} = useLocationContext();
    const {isMobile} = useScreenSizeContext();

    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (open && containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        const trimmed = input.trim();
        if (trimmed.length < 2) {
            setSuggestions([]);
            setSearching(false);
            return;
        }
        const requestId = ++requestIdRef.current;
        setSearching(true);
        const handle = setTimeout(async () => {
            try {
                const results = await geocodeQuery(trimmed);
                if (requestId === requestIdRef.current) {
                    setSuggestions(results);
                }
            } finally {
                if (requestId === requestIdRef.current) {
                    setSearching(false);
                }
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [input]);

    const handlePickSuggestion = (s: GeocodeSuggestion) => {
        setLocation({label: s.label, lat: s.lat, lon: s.lon, source: 'manual'});
        setInput('');
        setSuggestions([]);
        setOpen(false);
    };

    const handleCurrentLocation = async () => {
        setError(null);
        try {
            await requestCurrentLocation();
            setOpen(false);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not get your location');
        }
    };

    const handleClear = () => {
        setLocation(null);
        setInput('');
        setSuggestions([]);
    };

    const triggerLabel = location ? location.label : placeholderLabel;

    return (
        <div className='relative' ref={containerRef}>
            {variant === 'inline' ? (
                <button
                    className={`location-picker-inline-trigger ${location ? 'has-location' : ''}`}
                    onClick={() => setOpen(!open)}
                    type='button'
                >
                    <FaMapMarkerAlt className='location-picker-inline-pin'/>
                    <span className='location-picker-inline-label'>{triggerLabel}</span>
                    <FaChevronDown className='location-picker-inline-chevron'/>
                </button>
            ) : (
                <button
                    className={`filter-button ${location ? 'filtered' : ''}`}
                    onClick={() => setOpen(!open)}
                    type='button'
                >
                    {location ? `Shows Near ${location.label}` : placeholderLabel}
                </button>
            )}
            {open && (
                <div className={`${isMobile ? 'mobile-filter-menu' : 'dropdown-menu right-aligned'} location-picker-menu`}>
                    {isMobile && (
                        <div className='row' style={{justifyContent: 'center'}}>
                            <div className='text-s' style={{margin: '10px 0'}}>Choose Location</div>
                            <div
                                style={{position: 'absolute', top: '32px', right: '30px', fontSize: '24px', cursor: 'pointer'}}
                                onClick={() => setOpen(false)}
                            >
                                <FaTimes/>
                            </div>
                        </div>
                    )}
                    <input
                        autoFocus
                        type='text'
                        className='dropdown-input location-picker-input'
                        placeholder='City or ZIP'
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                    />
                    <button
                        type='button'
                        className='location-picker-item'
                        onClick={handleCurrentLocation}
                        disabled={isLocating}
                    >
                        <FaLocationArrow className='location-picker-icon'/>
                        <span>{isLocating ? 'Locating…' : 'Current Location'}</span>
                    </button>
                    {error && <div className='location-picker-error'>{error}</div>}
                    {searching && <div className='location-picker-status'>Searching…</div>}
                    {!searching && suggestions.length > 0 && (
                        <div className='location-picker-suggestions'>
                            {suggestions.map((s, idx) => (
                                <button
                                    type='button'
                                    key={`${s.lat}-${s.lon}-${idx}`}
                                    className='location-picker-item'
                                    onClick={() => handlePickSuggestion(s)}
                                >
                                    <FaMapMarkerAlt className='location-picker-icon'/>
                                    <span>{s.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {location && (
                        <button type='button' className='location-picker-clear' onClick={handleClear}>
                            Clear location
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default LocationPicker;
